import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Badge, Breadcrumb, Button, Card, Col, Form, Modal, Row, Spinner, Tab, Table, Tabs } from 'react-bootstrap'
import { FaLock, FaLockOpen } from 'react-icons/fa'
import { ResponsiveContainer, LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, Legend } from 'recharts'
import { useNavigate, useParams } from 'react-router-dom'
import useFetch from '../../../../_fetch'
import { formatLocalDateForInput, formatNumber, formatNumberLocale } from '../../../../_utils'
import { ClickValuePeriod, IPartnership } from '../types'
import { useAuth } from '../../../../auth/auth-context'
import DateRangeSelector from '../../home/dashboard-content/date-range-selector'
import { loadPref, resolveLabel } from '../../home/dashboard-content/date-range-preference'
import { getClickValuePeriodError } from './click-value-period-validation'

export { getClickValuePeriodError } from './click-value-period-validation'

type ConversionStatus = 'pending' | 'approved' | 'rejected'
type ConversionRow = {
  id: number
  publisher_user_id: string
  order_id: string
  amount_value: number | null
  commission_value: number | null
  currency: string | null
  received_at: string
  status: ConversionStatus
  click_ref?: string | null
  page_url?: string | null
}
type Analytics = {
  currency: string
  summary: {
    clicks: number
    conversions: number
    approvedOrderValue: number
    approvedCommission: number
    statuses: Record<ConversionStatus, number>
  }
  series: Array<{ date: string; clicks: number; conversions: number; approvedOrderValue: number; approvedCommission: number }>
  byPublisher: Array<{ userId: string; username: string; clicks: number; conversions: number; approvedCommission: number }>
  conversions: { items: ConversionRow[]; total: number; limit: number; offset: number }
}
type ClickValuePeriodForm = Omit<ClickValuePeriod, 'value'> & { value: string }
type PartnershipForm = {
  name: string
  domains: string
  value: string
  currency: string
  startDate: string
  expiryDate: string
  cookieDays: string
  locked: boolean
  clickValuePeriods: ClickValuePeriodForm[]
}
type PeriodEditor = { index: number | null; draft: ClickValuePeriodForm }

function settingsSnapshot(form: PartnershipForm) {
  const { clickValuePeriods, ...settings } = form
  return JSON.stringify(settings)
}

function initialRange() {
  const preferredRange = resolveLabel(loadPref('partnerships')) || resolveLabel('30 Days')
  if (preferredRange) {
    return {
      from: formatLocalDateForInput(preferredRange.startDate),
      to: formatLocalDateForInput(preferredRange.endDate),
    }
  }

  const to = new Date()
  const from = new Date()
  from.setDate(from.getDate() - 29)
  return { from: formatLocalDateForInput(from), to: formatLocalDateForInput(to) }
}
function errorMessage(value: unknown, fallback: string) {
  if (typeof value !== 'string') return fallback
  try { return JSON.parse(value)?.error || fallback } catch { return value || fallback }
}
function statusVariant(status: string) {
  return status === 'approved' ? 'success' : status === 'rejected' ? 'danger' : 'warning'
}
function lifecycleVariant(status?: string) {
  return status === 'active' ? 'success' : status === 'expired' || status === 'archived' || status === 'deleted' ? 'secondary' : status === 'awaiting_publisher' ? 'warning' : 'info'
}
function titleCase(value?: string) { return String(value || '').replaceAll('_', ' ').replace(/^./, (letter) => letter.toUpperCase()) }
function minDate(...values: Array<string | null | undefined>) {
  const dates = values.filter((value): value is string => !!value).sort()
  return dates[0] || undefined
}
function maxDate(...values: Array<string | null | undefined>) {
  const dates = values.filter((value): value is string => !!value).sort()
  return dates[dates.length - 1] || undefined
}
function shiftDate(value: string | null | undefined, days: number) {
  if (!value) return undefined
  const date = new Date(`${value}T00:00:00.000Z`)
  if (Number.isNaN(date.getTime())) return undefined
  date.setUTCDate(date.getUTCDate() + days)
  return date.toISOString().slice(0, 10)
}

export default function PartnershipDetail() {
  const { id, tab } = useParams()
  const navigate = useNavigate()
  const { doFetch } = useFetch()
  const { user } = useAuth()
  const range = useMemo(initialRange, [])
  const [partnership, setPartnership] = useState<IPartnership | null>(null)
  const [analytics, setAnalytics] = useState<Analytics | null>(null)
  const [loading, setLoading] = useState(true)
  const [analyticsLoading, setAnalyticsLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [settingsError, setSettingsError] = useState('')
  const [settingsSuccess, setSettingsSuccess] = useState('')
  const [isEditingSettings, setIsEditingSettings] = useState(false)
  const [from, setFrom] = useState(range.from)
  const [to, setTo] = useState(range.to)
  const [status, setStatus] = useState('')
  const [offset, setOffset] = useState(0)
  const [details, setDetails] = useState<ConversionRow | null>(null)
  const [showDelete, setShowDelete] = useState(false)
  const [deletionImpact, setDeletionImpact] = useState<{ conversions: number; clicks: number; hasActivity: boolean; requiresArchive: boolean } | null>(null)
  const [actionLoading, setActionLoading] = useState(false)
  const [pdfDownloading, setPdfDownloading] = useState(false)
  const [settingsBaseline, setSettingsBaseline] = useState('')
  const [settingsLoading, setSettingsLoading] = useState(false)
  const [periodEditor, setPeriodEditor] = useState<PeriodEditor | null>(null)
  const [periodSaving, setPeriodSaving] = useState(false)
  const [periodError, setPeriodError] = useState('')
  const [periodSuccess, setPeriodSuccess] = useState('')
  const allowPopNavigation = useRef(false)
  const loadedDetailId = useRef('')
  const previousTab = useRef(tab)
  const isEditingSettingsRef = useRef(false)
  const periodEditorRef = useRef<PeriodEditor | null>(null)
  const [form, setForm] = useState<PartnershipForm>({ name: '', domains: '', value: '', currency: 'DKK', startDate: '', expiryDate: '', cookieDays: '30', locked: false, clickValuePeriods: [] })

  const populate = (data: IPartnership) => {
    setPartnership(data)
    const nextForm: PartnershipForm = {
      name: data.name || '', domains: (data.domains || []).join(', '), value: String(data.value ?? ''),
      currency: data.currency || 'DKK', startDate: data.startDate || '', expiryDate: data.expiryDate || '',
      cookieDays: String(data.cookieDays ?? 30), locked: data.locked === true,
      clickValuePeriods: (data.clickValuePeriods || []).map((period) => ({ ...period, value: String(period.value) })),
    }
    setForm(nextForm)
    setSettingsBaseline(settingsSnapshot(nextForm))
  }

  const beginEditingSettings = () => {
    allowPopNavigation.current = false
    setSettingsError('')
    setSettingsSuccess('')
    setIsEditingSettings(true)
  }

  const cancelEditingSettings = () => {
    if (partnership) populate(partnership)
    setSettingsError('')
    setSettingsSuccess('')
    setIsEditingSettings(false)
  }

  const editClickValuePeriod = (index: number) => {
    if (periodEditor) { setPeriodError('Save or cancel the current CPC period before editing another.'); return }
    setPeriodError('')
    setPeriodSuccess('')
    const editor = { index, draft: { ...form.clickValuePeriods[index] } }
    periodEditorRef.current = editor
    setPeriodEditor(editor)
  }

  const addClickValuePeriod = () => {
    if (!partnership?.permissions?.canEdit) return
    if (periodEditor) { setPeriodError('Save or cancel the current CPC period before adding another.'); return }
    setPeriodError('')
    setPeriodSuccess('')
    const editor = { index: null, draft: { startDate: '', endDate: '', value: String(partnership.value ?? '') } }
    periodEditorRef.current = editor
    setPeriodEditor(editor)
  }

  const candidateClickValuePeriods = (editor: PeriodEditor | null = periodEditor) => {
    if (!editor) return form.clickValuePeriods
    const periods = editor.index == null
      ? [...form.clickValuePeriods, editor.draft]
      : form.clickValuePeriods.map((period, index) => index === editor.index ? editor.draft : period)
    return [...periods].sort((first, second) => first.startDate.localeCompare(second.startDate) || first.endDate.localeCompare(second.endDate))
  }

  const persistClickValuePeriods = async (periods: ClickValuePeriodForm[], message: string) => {
    if (!partnership) return
    const validationError = getClickValuePeriodError(partnership.startDate, partnership.expiryDate || '', periods)
    if (validationError) { setPeriodError(validationError); setPeriodSuccess(''); return }
    try {
      setPeriodSaving(true); setPeriodError(''); setPeriodSuccess('')
      const response = await doFetch(`/user/partnerships/${partnership.id}`, {
        clickValuePeriods: periods.map((period) => ({ ...period, value: Number(period.value) })),
      }, 'PUT')
      if (!response.ok) throw await response.text()
      const updated: IPartnership = await response.json()
      const savedPeriods = (updated.clickValuePeriods || []).map((period) => ({ ...period, value: String(period.value) }))
      setPartnership(updated)
      setForm((current) => ({ ...current, clickValuePeriods: savedPeriods }))
      periodEditorRef.current = null
      setPeriodEditor(null)
      setPeriodSuccess(message)
    } catch (err) { setPeriodError(errorMessage(err, 'Could not save CPC periods')) } finally { setPeriodSaving(false) }
  }

  const saveClickValuePeriod = () => persistClickValuePeriods(candidateClickValuePeriods(), periodEditor?.index == null ? 'CPC period added' : 'CPC period updated')

  const removeClickValuePeriod = (index: number) => {
    if (!window.confirm('Remove this CPC period?')) return
    return persistClickValuePeriods(form.clickValuePeriods.filter((_, periodIndex) => periodIndex !== index), 'CPC period removed')
  }

  const cancelClickValuePeriod = () => {
    periodEditorRef.current = null
    setPeriodEditor(null)
    setPeriodError('')
  }

  const loadDetail = useCallback(async () => {
    if (!id) return
    try {
      setLoading(loadedDetailId.current !== id)
      const response = await doFetch(`/user/partnerships/${id}`)
      if (!response.ok) throw await response.text()
      populate(await response.json())
      loadedDetailId.current = id
    } catch (err) { setError(errorMessage(err, 'Could not load partnership')) } finally { setLoading(false) }
  }, [doFetch, id])

  const loadAnalytics = useCallback(async (nextOffset = offset) => {
    if (!id) return
    try {
      setAnalyticsLoading(true)
      const query = new URLSearchParams({ from, to, limit: '50', offset: String(nextOffset) })
      if (status) query.set('status', status)
      const response = await doFetch(`/user/partnerships/${id}/analytics?${query}`)
      if (!response.ok) throw await response.text()
      setAnalytics(await response.json())
      setOffset(nextOffset)
    } catch (err) { setError(errorMessage(err, 'Could not load partnership performance')) } finally { setAnalyticsLoading(false) }
  }, [doFetch, from, id, offset, status, to])

  useEffect(() => { loadDetail() }, [loadDetail])
  useEffect(() => { if (partnership && (tab === 'overview' || (tab === 'conversions' && partnership.type !== 'click'))) loadAnalytics(0) }, [partnership?.id, from, to, status, tab]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { isEditingSettingsRef.current = isEditingSettings }, [isEditingSettings])
  useEffect(() => { periodEditorRef.current = periodEditor }, [periodEditor])

  useEffect(() => {
    const enteredSettings = tab === 'settings' && previousTab.current !== 'settings'
    previousTab.current = tab
    if (!enteredSettings || !id || !partnership) return
    let cancelled = false
    setSettingsLoading(true)
    doFetch(`/user/partnerships/${id}`)
      .then(async (response) => {
        if (!response.ok) throw await response.text()
        const refreshed = await response.json()
        if (!cancelled && !isEditingSettingsRef.current && !periodEditorRef.current) populate(refreshed)
      })
      .catch((err) => { if (!cancelled) setSettingsError(errorMessage(err, 'Could not refresh partnership settings')) })
      .finally(() => { if (!cancelled) setSettingsLoading(false) })
    return () => { cancelled = true }
  }, [doFetch, id, partnership, tab])

  const save = async () => {
    if (!partnership) return
    const clickPeriodError = partnership.type === 'click' ? getClickValuePeriodError(form.startDate, form.expiryDate, form.clickValuePeriods) : ''
    if (clickPeriodError) { setSettingsError(clickPeriodError); setSettingsSuccess(''); return }
    try {
      setSaving(true); setSettingsError(''); setSettingsSuccess('')
      const response = await doFetch(`/user/partnerships/${partnership.id}`, {
        name: form.name,
        domains: form.domains.split(',').map((item) => item.trim()).filter(Boolean),
        value: Number(form.value),
        clickValuePeriods: partnership.type === 'click'
          ? form.clickValuePeriods.map((period) => ({ ...period, value: Number(period.value) }))
          : [],
        currency: form.currency,
        startDate: form.startDate,
        expiryDate: form.expiryDate || null,
        cookieDays: partnership.type === 'click' ? 0 : Number(form.cookieDays),
        ...(partnership.permissions?.canChangeLock ? { locked: form.locked } : {}),
      }, 'PUT')
      if (!response.ok) throw await response.text()
      populate(await response.json())
      setIsEditingSettings(false)
      setSettingsSuccess('Partnership settings saved')
    } catch (err) { setSettingsError(errorMessage(err, 'Could not save partnership')) } finally { setSaving(false) }
  }

  const rotateSecret = async () => {
    if (!partnership || !window.confirm('Rotate the Server-side Conversion Secret Key? Existing server-side and legacy integrations must be updated.')) return
    try {
      const response = await doFetch(`/user/partnerships/${partnership.id}/regenerate-secret`, {})
      if (!response.ok) throw await response.text()
      populate(await response.json())
      setSuccess('Server-side Conversion Secret Key rotated')
    } catch (err) { setError(errorMessage(err, 'Could not rotate secret')) }
  }

  const remove = async () => {
    if (!partnership) return
    try {
      setActionLoading(true)
      const endpoint = deletionImpact?.requiresArchive ? `/user/partnerships/${partnership.id}/archive` : `/user/partnerships/${partnership.id}`
      const response = deletionImpact?.requiresArchive
        ? await doFetch(endpoint, {}, 'POST')
        : await doFetch(endpoint, undefined, 'DELETE')
      if (response.status === 409) {
        const impact = await response.json().catch(() => null)
        if (impact?.code === 'PARTNERSHIP_HAS_ACTIVITY') {
          setDeletionImpact({ conversions: Number(impact.conversions || 0), clicks: Number(impact.clicks || 0), hasActivity: true, requiresArchive: true })
          return
        }
      }
      if (!response.ok) throw await response.text()
      if (deletionImpact?.requiresArchive) {
        populate(await response.json())
        setShowDelete(false)
        setSuccess('Partnership archived. Historical data remains available.')
      } else navigate('/partnerships')
    } catch (err) { setError(errorMessage(err, 'Could not update partnership')); setShowDelete(false) } finally { setActionLoading(false) }
  }

  const inspectDeletion = async () => {
    if (!partnership) return
    try {
      setActionLoading(true)
      const response = await doFetch(`/user/partnerships/${partnership.id}/deletion-impact`)
      if (!response.ok) throw await response.text()
      setDeletionImpact(await response.json())
      setShowDelete(true)
    } catch (err) { setError(errorMessage(err, 'Could not inspect partnership activity')) } finally { setActionLoading(false) }
  }

  const restore = async () => {
    if (!partnership) return
    try {
      setActionLoading(true)
      const response = await doFetch(`/user/partnerships/${partnership.id}/restore`, {}, 'POST')
      if (!response.ok) throw await response.text()
      populate(await response.json())
      setSuccess('Partnership restored')
    } catch (err) { setError(errorMessage(err, 'Could not restore partnership')) } finally { setActionLoading(false) }
  }

  const downloadPdf = async () => {
    if (!partnership || pdfDownloading) return
    try {
      setPdfDownloading(true)
      setError('')
      const response = await doFetch(`/user/partnerships/${partnership.id}/integration-pdf`)
      if (!response.ok) throw await response.text()
      const url = URL.createObjectURL(await response.blob())
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = `${partnership.name.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-integration.pdf`
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
      window.setTimeout(() => URL.revokeObjectURL(url), 0)
    } catch (err) {
      setError(errorMessage(err, 'Could not download integration PDF'))
    } finally {
      setPdfDownloading(false)
    }
  }

  const updateConversionStatus = async (row: ConversionRow, nextStatus: ConversionStatus) => {
    if (!partnership) return
    try {
      const response = await doFetch(`/user/partnerships/${partnership.id}/conversions/${row.id}/status`, { status: nextStatus }, 'PATCH')
      if (!response.ok) throw await response.text()
      await loadAnalytics(offset)
      setDetails((current) => current?.id === row.id ? { ...current, status: nextStatus } : current)
    } catch (err) { setError(errorMessage(err, 'Could not update conversion status')) }
  }

  const isSettingsDirty = (isEditingSettings && settingsSnapshot(form) !== settingsBaseline) || !!periodEditor
  const currentTab = tab === 'settings' || tab === 'conversions' ? tab : 'overview'

  useEffect(() => {
    setIsEditingSettings(false)
    periodEditorRef.current = null
    setPeriodEditor(null)
    setPeriodError('')
    setPeriodSuccess('')
    setSettingsError('')
    setSettingsSuccess('')
    if (tab === 'settings' && partnership) populate(partnership)
  }, [partnership?.id, tab]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (tab && !['overview', 'conversions', 'settings'].includes(tab) && partnership) navigate(`/partnerships/${partnership.id}/overview`, { replace: true })
    if (partnership?.type === 'click' && tab === 'conversions') navigate(`/partnerships/${partnership.id}/overview`, { replace: true })
  }, [navigate, partnership, tab])

  useEffect(() => {
    if (!isSettingsDirty) return
    const beforeUnload = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = '' }
    const guardLinks = (event: MouseEvent) => {
      const anchor = (event.target as HTMLElement | null)?.closest('a[href]') as HTMLAnchorElement | null
      if (!anchor || anchor.href === window.location.href) return
      if (!window.confirm('Discard your unsaved partnership changes?')) { event.preventDefault(); event.stopPropagation() }
    }
    const guardHistory = () => {
      if (allowPopNavigation.current) return
      if (window.confirm('Discard your unsaved partnership changes?')) {
        allowPopNavigation.current = true
        setIsEditingSettings(false)
        return
      }
      navigate(`/partnerships/${id}/settings`, { replace: true })
    }
    window.addEventListener('beforeunload', beforeUnload)
    window.addEventListener('popstate', guardHistory)
    document.addEventListener('click', guardLinks, true)
    return () => { window.removeEventListener('beforeunload', beforeUnload); window.removeEventListener('popstate', guardHistory); document.removeEventListener('click', guardLinks, true) }
  }, [id, isSettingsDirty, navigate])

  const selectTab = (nextTab: string) => {
    if (!id || nextTab === currentTab) return
    if (isSettingsDirty && !window.confirm('Discard your unsaved partnership changes?')) return
    navigate(`/partnerships/${id}/${nextTab}`)
  }

  if (loading) return <div className="d-flex justify-content-center py-5"><Spinner animation="border" /></div>
  if (!partnership) return <Alert variant="danger">{error || 'Partnership not found'}</Alert>
  const permissions = partnership.permissions
  const visibleLifecycle = partnership.lifecycle || 'active'
  const primaryPublisher = partnership.publisher_users[0]
  const summary = analytics?.summary
  const displayCurrency = analytics?.currency || partnership.currency
  const totalPages = Math.max(1, Math.ceil((analytics?.conversions.total || 0) / (analytics?.conversions.limit || 50)))
  const currentPage = Math.floor(offset / (analytics?.conversions.limit || 50)) + 1
  const settingsDisabled = !permissions?.canEdit || !isEditingSettings
  const periodStarts = form.clickValuePeriods.map((period) => period.startDate).filter(Boolean).sort()
  const periodEnds = form.clickValuePeriods.map((period) => period.endDate).filter(Boolean).sort()
  const earliestPeriodStart = periodStarts[0]
  const latestPeriodEnd = periodEnds[periodEnds.length - 1]
  const livePeriodError = isEditingSettings && partnership.type === 'click'
    ? getClickValuePeriodError(form.startDate, form.expiryDate, form.clickValuePeriods)
    : ''
  const periodDraftError = periodEditor
    ? getClickValuePeriodError(partnership.startDate, partnership.expiryDate || '', candidateClickValuePeriods())
    : ''
  const downloadPdfButton = (
    <Button variant="outline-secondary" disabled={pdfDownloading} aria-busy={pdfDownloading} onClick={downloadPdf}>
      {pdfDownloading
        ? <><Spinner as="span" animation="border" size="sm" className="me-2" aria-hidden="true" />Preparing PDF…</>
        : 'Download setup PDF'}
    </Button>
  )

  return <div className="container-fluid px-2 px-md-3 partnership-detail-page partnership-page-shell">
    {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
    {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}
    <Card className="mb-4 border-0 shadow-sm partnership-hero-card">
      <Card.Body className="d-flex flex-column flex-lg-row justify-content-between gap-3">
        <div>
          <Breadcrumb className="mb-1 small"><Breadcrumb.Item onClick={() => navigate('/partnerships')}>Partnerships</Breadcrumb.Item><Breadcrumb.Item active>{partnership.name}</Breadcrumb.Item></Breadcrumb>
          <div className="d-flex flex-wrap align-items-center gap-2">
            <h4 className="mb-0">{partnership.name}</h4>
            <Badge bg={lifecycleVariant(visibleLifecycle)}>{titleCase(visibleLifecycle)}</Badge>
            <Badge bg="dark">{titleCase(partnership.type)}</Badge>
            {partnership.locked && <Badge bg="secondary"><FaLock className="me-1" />Advertiser managed</Badge>}
          </div>
          {permissions?.canViewAllPublishers && primaryPublisher && <div className="text-muted small mt-2">Publisher: {primaryPublisher.username}</div>}
          {!permissions?.canViewAllPublishers && partnership.advertiser && <div className="text-muted small mt-2">Advertiser: {partnership.advertiser.username}</div>}
        </div>
        {currentTab !== 'settings' && <div className="partnership-date-controls">
          <div className="small text-muted mb-1">Reporting period</div>
          <DateRangeSelector
            pageKey="partnerships"
            defaultLabel="30 Days"
            onSubmit={async (startDate, endDate) => {
              setFrom(formatLocalDateForInput(startDate))
              setTo(formatLocalDateForInput(endDate))
              setOffset(0)
            }}
          />
        </div>}
      </Card.Body>
    </Card>

    {permissions?.isLegacyOwnershipAmbiguous && <Alert variant="secondary">This legacy partnership has multiple publishers and no recorded creator. Partnership settings are therefore managed by admin.</Alert>}
    {user?.kind === 'advertiser' && partnership.lifecycle === 'awaiting_publisher' && <Alert variant="warning"><strong>Awaiting publisher.</strong> Tracking becomes active after an admin attaches a publisher.</Alert>}

    <Tabs activeKey={currentTab} onSelect={(key) => selectTab(key || 'overview')} className="mb-3 partnership-tabs">
      <Tab eventKey="overview" title="Overview" />
      {partnership.type !== 'click' && <Tab eventKey="conversions" title={`Conversions${analytics?.conversions.total ? ` (${analytics.conversions.total})` : ''}`} />}
      <Tab eventKey="settings" title="Setup & settings" />
    </Tabs>

    {currentTab === 'overview' && <>
      <Row className="g-3 mb-3">
        {[
          ['Clicks', formatNumberLocale(summary?.clicks || 0, 0)],
          ...(partnership.type === 'click' ? [] : [['Conversions', formatNumberLocale(summary?.conversions || 0, 0)]]),
          ...(partnership.type !== 'click' && user?.kind !== 'publisher' ? [['Approved revenue', formatNumber(summary?.approvedOrderValue || 0, displayCurrency)]] : []),
          [user?.kind === 'publisher' ? 'Commission' : 'Approved commission', formatNumber(summary?.approvedCommission || 0, displayCurrency)],
          ...(partnership.type === 'click' ? [] : [['Pending', formatNumberLocale(summary?.statuses.pending || 0, 0)]]),
        ].map(([label, value]) => <Col xs={6} lg key={String(label)}><Card className="border-0 h-100 partnership-kpi-card"><Card.Body className="py-3 text-center"><div className="fs-5 fw-bold">{value}</div><div className="text-muted small">{label}</div></Card.Body></Card></Col>)}
      </Row>
      <Card className="mb-4 border-0 shadow-sm partnership-panel"><Card.Body><div className="text-uppercase fw-bold mb-3 partnership-section-title">Performance over time</div>{analyticsLoading ? <div className="text-center py-5"><Spinner animation="border" /></div> : analytics?.series.length ? <div style={{ height: 330 }}><ResponsiveContainer><LineChart data={analytics.series}><CartesianGrid strokeDasharray="3 3" vertical={false} /><XAxis dataKey="date" /><YAxis yAxisId="count" allowDecimals={false} tick={{ fontSize: 11 }} tickFormatter={(value) => formatNumberLocale(Number(value), 0)} width={48} /><YAxis yAxisId="money" orientation="right" tick={{ fontSize: 11 }} tickFormatter={(value) => formatNumber(Number(value), displayCurrency)} width={92} /><Tooltip formatter={(value: any, name: any) => name === 'Commission' ? formatNumber(Number(value), displayCurrency) : formatNumberLocale(Number(value), 0)} /><Legend /><Line yAxisId="count" type="monotone" dataKey="clicks" name="Clicks" stroke="#5b9bd5" strokeWidth={2} dot={false} />{partnership.type !== 'click' && <Line yAxisId="count" type="monotone" dataKey="conversions" name="Conversions" stroke="#198754" strokeWidth={2} dot={false} />}<Line yAxisId="money" type="monotone" dataKey="approvedCommission" name="Commission" stroke="#6f42c1" strokeWidth={2} dot={false} /></LineChart></ResponsiveContainer></div> : <div className="text-center text-muted py-5">No performance data in this period.</div>}</Card.Body></Card>
      {partnership.type !== 'click' && permissions?.canViewAllPublishers && !!analytics?.byPublisher.length && <Card className="border-0 shadow-sm partnership-panel"><Card.Body><div className="text-uppercase fw-bold mb-3 partnership-section-title">Publisher performance</div><Table responsive hover size="sm"><thead><tr><th>Publisher</th><th>Clicks</th><th>Conversions</th><th>Approved commission</th></tr></thead><tbody>{analytics.byPublisher.slice(0, 1).map((row) => <tr key={row.userId}><td>{row.username}</td><td>{formatNumberLocale(row.clicks, 0)}</td><td>{formatNumberLocale(row.conversions, 0)}</td><td>{formatNumber(row.approvedCommission, analytics.currency)}</td></tr>)}</tbody></Table></Card.Body></Card>}
    </>}

    {currentTab === 'conversions' && partnership.type !== 'click' && <Card className="border-0 shadow-sm partnership-panel"><Card.Body>
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-2 mb-3"><div><div className="text-uppercase fw-bold partnership-section-title">Conversions</div><div className="text-muted small">Publishers only see their own data.</div></div><Form.Select aria-label="Conversion status" size="sm" value={status} onChange={(e) => setStatus(e.target.value)} className="partnership-status-filter"><option value="">All statuses</option><option value="pending">Pending</option><option value="approved">Approved</option><option value="rejected">Rejected</option></Form.Select></div>
      {analyticsLoading ? <div className="text-center py-5"><Spinner animation="border" /></div> : <Table responsive hover className="align-middle"><thead><tr><th>Received</th><th>Order</th><th>Publisher</th><th>Amount</th><th>Commission</th><th>Status</th><th /></tr></thead><tbody>{analytics?.conversions.items.length ? analytics.conversions.items.map((row) => <tr key={row.id}><td>{new Date(row.received_at).toLocaleString('da-DK')}</td><td>{row.order_id}</td><td>{primaryPublisher?.username || row.publisher_user_id}</td><td>{formatNumber(Number(row.amount_value || 0), row.currency || partnership.currency)}</td><td>{formatNumber(Number(row.commission_value || 0), row.currency || partnership.currency)}</td><td><Badge bg={statusVariant(row.status)}>{titleCase(row.status)}</Badge></td><td className="text-end"><Button size="sm" variant="outline-dark" onClick={() => setDetails(row)}>Details</Button></td></tr>) : <tr><td colSpan={7} className="text-center text-muted py-5">No conversions found.</td></tr>}</tbody></Table>}
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-2"><span className="text-muted small">Page {currentPage} of {totalPages}</span><div className="d-flex gap-2 partnership-pagination-actions"><Button size="sm" variant="outline-secondary" disabled={currentPage <= 1} onClick={() => loadAnalytics(Math.max(0, offset - 50))}>Previous</Button><Button size="sm" variant="outline-secondary" disabled={currentPage >= totalPages} onClick={() => loadAnalytics(offset + 50)}>Next</Button></div></div>
    </Card.Body></Card>}

    {currentTab === 'settings' && <>
      {settingsSuccess && <Alert variant="success" dismissible onClose={() => setSettingsSuccess('')}>{settingsSuccess}</Alert>}
      {!permissions?.canEdit && <Alert variant="secondary">You have read-only access to these partnership settings.</Alert>}

      <Card className="mb-3 border-0 shadow-sm partnership-panel"><Card.Body>
        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-2 mb-3"><div><div className="text-uppercase fw-bold partnership-section-title">Agreement</div><div className="text-muted small">Core details, payout and active dates.</div></div><div className="d-flex align-items-center gap-2">{settingsLoading && <Spinner animation="border" size="sm" title="Refreshing settings" />}{permissions?.canEdit && !isEditingSettings && <Button size="sm" variant="outline-primary" onClick={beginEditingSettings}>Edit settings</Button>}</div></div>
        <Row className="g-3">
          <Col md={6}><Form.Label>Name</Form.Label><Form.Control value={form.name} disabled={settingsDisabled} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Col>
          <Col md={6}><Form.Label>Domains</Form.Label><Form.Control value={form.domains} disabled={settingsDisabled} onChange={(e) => setForm({ ...form, domains: e.target.value })} placeholder="example.com, shop.example.com" /></Col>
          <Col md={4}><Form.Label>Type</Form.Label><Form.Control value={titleCase(partnership.type)} disabled /></Col>
          <Col md={4}><Form.Label>{partnership.type === 'click' ? 'Default value per click' : 'Value'}</Form.Label><Form.Control type="number" min={0} step="0.01" value={form.value} disabled={settingsDisabled} onChange={(e) => setForm({ ...form, value: e.target.value })} /></Col>
          <Col md={4}><Form.Label>Currency</Form.Label><Form.Control value={form.currency} maxLength={3} disabled={settingsDisabled} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} /></Col>
          <Col md={4}><Form.Label>Start date</Form.Label><Form.Control type="date" value={form.startDate} max={minDate(form.expiryDate, earliestPeriodStart)} disabled={settingsDisabled} onChange={(e) => setForm({ ...form, startDate: e.target.value })} /></Col>
          <Col md={4}><Form.Label>End date</Form.Label><Form.Control type="date" value={form.expiryDate} min={maxDate(form.startDate, latestPeriodEnd)} disabled={settingsDisabled} onChange={(e) => setForm({ ...form, expiryDate: e.target.value })} /></Col>
          {partnership.type !== 'click' && <Col md={4}><Form.Label>Cookie days</Form.Label><Form.Control type="number" min={1} max={60} value={form.cookieDays} disabled={settingsDisabled} onChange={(e) => setForm({ ...form, cookieDays: e.target.value })} /></Col>}
          {permissions?.canChangeLock && partnership.advertiser && <Col xs={12}><Form.Check type="switch" id="partnership-lock" checked={form.locked} disabled={settingsDisabled} onChange={(e) => setForm({ ...form, locked: e.target.checked })} label={<span>{form.locked ? <FaLock className="me-2" /> : <FaLockOpen className="me-2" />}Only advertiser can edit partnership settings</span>} /></Col>}
        </Row>
        {isEditingSettings && <div className="mt-4 pt-3 border-top">
          {settingsError && <Alert variant="danger" className="mb-3">{settingsError}</Alert>}
          <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-2"><div className="text-muted small">Changes to agreement data are not applied until you save.</div><div className="d-flex gap-2 partnership-form-actions"><Button variant="outline-secondary" onClick={cancelEditingSettings} disabled={saving}>Cancel</Button><Button onClick={save} disabled={saving || !!livePeriodError}>{saving ? 'Saving…' : 'Save changes'}</Button></div></div>
        </div>}
      </Card.Body></Card>

      {partnership.type === 'click' && <Card className="mb-3 border-0 shadow-sm partnership-panel"><Card.Body>
        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-2 mb-3 partnership-section-header"><div><div className="text-uppercase fw-bold partnership-section-title">CPC periods</div><div className="text-muted small">Override the default CPC for inclusive, non-overlapping date ranges inside the agreement period.</div></div><Button size="sm" variant="outline-primary" disabled={!permissions?.canEdit || periodSaving} onClick={addClickValuePeriod}>Add period</Button></div>
        {periodSuccess && <Alert variant="success" dismissible onClose={() => setPeriodSuccess('')} className="py-2">{periodSuccess}</Alert>}
        {(form.clickValuePeriods.length > 0 || !!periodEditor) ? <Table responsive size="sm" className="align-middle partnership-period-table mb-0"><thead><tr><th>Start date</th><th>End date</th><th>Value per click</th><th>Currency</th><th /></tr></thead><tbody>{form.clickValuePeriods.map((period, index) => {
          const isCurrentEditor = periodEditor?.index === index
          const displayedPeriod = isCurrentEditor ? periodEditor.draft : period
          const previousEnd = form.clickValuePeriods[index - 1]?.endDate
          const nextStart = form.clickValuePeriods[index + 1]?.startDate
          return <tr key={`${period.startDate}-${period.endDate}-${index}`}>
          <td>{isCurrentEditor ? <Form.Control aria-label={`CPC period ${index + 1} start date`} size="sm" type="date" value={displayedPeriod.startDate} min={maxDate(partnership.startDate, shiftDate(previousEnd, 1))} max={minDate(displayedPeriod.endDate, partnership.expiryDate, shiftDate(nextStart, -1))} disabled={periodSaving} onChange={(e) => setPeriodEditor({ index, draft: { ...displayedPeriod, startDate: e.target.value } })} /> : period.startDate}</td>
          <td>{isCurrentEditor ? <Form.Control aria-label={`CPC period ${index + 1} end date`} size="sm" type="date" value={displayedPeriod.endDate} min={maxDate(partnership.startDate, displayedPeriod.startDate, shiftDate(previousEnd, 1))} max={minDate(partnership.expiryDate, shiftDate(nextStart, -1))} disabled={periodSaving} onChange={(e) => setPeriodEditor({ index, draft: { ...displayedPeriod, endDate: e.target.value } })} /> : period.endDate}</td>
          <td>{isCurrentEditor ? <Form.Control aria-label={`CPC period ${index + 1} value`} size="sm" type="number" min={0} step="0.01" value={displayedPeriod.value} disabled={periodSaving} onChange={(e) => setPeriodEditor({ index, draft: { ...displayedPeriod, value: e.target.value } })} /> : period.value}</td>
          <td><span className="partnership-currency-pill">{partnership.currency}</span></td>
          <td className="text-end text-nowrap">{isCurrentEditor ? <div className="d-flex justify-content-end gap-1"><Button size="sm" variant="primary" disabled={periodSaving || !!periodDraftError} onClick={saveClickValuePeriod}>{periodSaving ? 'Saving…' : 'Save'}</Button><Button size="sm" variant="outline-secondary" disabled={periodSaving} onClick={cancelClickValuePeriod}>Cancel</Button><Button size="sm" variant="link" className="text-danger text-decoration-none" disabled={periodSaving} onClick={() => removeClickValuePeriod(index)}>Remove</Button></div> : permissions?.canEdit && <Button size="sm" variant="outline-secondary" disabled={periodSaving} onClick={() => editClickValuePeriod(index)}>Edit</Button>}</td>
        </tr>})}
        {periodEditor?.index === null && <tr>
          <td><Form.Control aria-label="New CPC period start date" size="sm" type="date" value={periodEditor.draft.startDate} min={partnership.startDate} max={minDate(periodEditor.draft.endDate, partnership.expiryDate)} disabled={periodSaving} onChange={(e) => setPeriodEditor({ index: null, draft: { ...periodEditor.draft, startDate: e.target.value } })} /></td>
          <td><Form.Control aria-label="New CPC period end date" size="sm" type="date" value={periodEditor.draft.endDate} min={maxDate(partnership.startDate, periodEditor.draft.startDate)} max={partnership.expiryDate || undefined} disabled={periodSaving} onChange={(e) => setPeriodEditor({ index: null, draft: { ...periodEditor.draft, endDate: e.target.value } })} /></td>
          <td><Form.Control aria-label="New CPC period value" size="sm" type="number" min={0} step="0.01" value={periodEditor.draft.value} disabled={periodSaving} onChange={(e) => setPeriodEditor({ index: null, draft: { ...periodEditor.draft, value: e.target.value } })} /></td>
          <td><span className="partnership-currency-pill">{partnership.currency}</span></td>
          <td className="text-end"><div className="d-flex justify-content-end gap-1"><Button size="sm" variant="primary" disabled={periodSaving || !!periodDraftError} onClick={saveClickValuePeriod}>{periodSaving ? 'Saving…' : 'Save'}</Button><Button size="sm" variant="outline-secondary" disabled={periodSaving} onClick={cancelClickValuePeriod}>Cancel</Button></div></td>
        </tr>}</tbody></Table> : <div className="partnership-empty-state"><div className="fw-semibold mb-1">Default CPC applies to every click</div><div className="text-muted small">Add a period when a specific date range should use another value.</div></div>}
        {(periodError || periodDraftError) && <Alert variant="warning" className="mt-3 mb-0">{periodError || periodDraftError}</Alert>}
      </Card.Body></Card>}

      {partnership.type !== 'click' && <Card className="mb-4 border-0 shadow-sm partnership-panel"><Card.Body><div className="text-uppercase fw-bold mb-2 partnership-section-title">Conversion setup</div>{permissions?.canViewSecret ? <><div className="text-muted small mb-3">The Server-side Conversion Secret Key is only used by backend and legacy integrations. Rotating it immediately invalidates the previous key.</div><Row className="g-2"><Col md><Form.Control aria-label="Server-side Conversion Secret Key" value={partnership.scriptSecret || ''} disabled className="font-monospace" /></Col><Col md="auto"><Button variant="outline-danger" disabled={!permissions?.canRotateSecret} onClick={rotateSecret}>Rotate key</Button></Col><Col md="auto">{downloadPdfButton}</Col></Row></> : <><div className="text-muted small mb-3">The Server-side Conversion Secret Key is not shown to publishers in the dashboard. It is included in the protected setup PDF.</div>{permissions?.canDownloadIntegrationPdf && downloadPdfButton}</>}</Card.Body></Card>}
      {(permissions?.canDelete || permissions?.canArchive || permissions?.canRestore) && <div className="d-flex justify-content-end mb-5 pt-2">
        {permissions?.canRestore
          ? <Button size="sm" variant="outline-secondary" disabled={actionLoading} onClick={restore}>{actionLoading ? 'Restoring…' : 'Restore partnership'}</Button>
          : <Button size="sm" variant="outline-secondary" className="partnership-delete-button" disabled={actionLoading} onClick={inspectDeletion}>{actionLoading ? 'Checking activity…' : 'Delete partnership'}</Button>}
      </div>}
    </>}

    <Modal show={!!details} onHide={() => setDetails(null)} size="lg"><Modal.Header closeButton><Modal.Title>Conversion details</Modal.Title></Modal.Header><Modal.Body>{details && <><Row className="g-3"><Col md={6}><div className="text-muted small">Order ID</div><div>{details.order_id}</div></Col><Col md={6}><div className="text-muted small">Received</div><div>{new Date(details.received_at).toLocaleString('da-DK')}</div></Col><Col md={6}><div className="text-muted small">Click reference</div><div>{details.click_ref || '—'}</div></Col><Col md={6}><div className="text-muted small">Page</div><div className="text-break">{details.page_url || '—'}</div></Col></Row>{permissions?.canManageConversions && <Form.Group className="mt-4"><Form.Label>Status</Form.Label><Form.Select value={details.status} onChange={(e) => updateConversionStatus(details, e.target.value as ConversionStatus)}><option value="pending">Pending</option><option value="approved">Approved</option><option value="rejected">Rejected</option></Form.Select></Form.Group>}</>}</Modal.Body></Modal>
    <Modal show={showDelete} onHide={() => setShowDelete(false)}><Modal.Header closeButton><Modal.Title>{deletionImpact?.requiresArchive ? 'Archive partnership' : 'Delete partnership'}</Modal.Title></Modal.Header><Modal.Body>{deletionImpact?.requiresArchive ? <><p><strong>{partnership.name}</strong> has recorded conversions and cannot be deleted.</p><div className="rounded bg-light p-3 mb-3"><div>Conversions: <strong>{deletionImpact.conversions}</strong></div><div>Clicks: <strong>{deletionImpact.clicks}</strong></div></div><p className="mb-0 text-muted small">It will be archived so conversions, commission and analytics remain available.</p></> : <><p><strong>{partnership.name}</strong> can be deleted because it has no conversion history{partnership.type === 'click' ? ' and is a Click partnership' : ''}.</p>{!!deletionImpact?.clicks && <div className="rounded bg-light p-3 mb-3">Recorded clicks: <strong>{deletionImpact.clicks}</strong></div>}<p className="mb-0 text-muted small">It will be marked as Deleted, stop receiving new activity and remain visible at the bottom of the Partnerships list.</p></>}</Modal.Body><Modal.Footer><Button variant="secondary" onClick={() => setShowDelete(false)} disabled={actionLoading}>Cancel</Button><Button variant="danger" disabled={actionLoading} onClick={remove}>{actionLoading ? 'Working…' : deletionImpact?.requiresArchive ? 'Archive partnership' : 'Delete partnership'}</Button></Modal.Footer></Modal>
  </div>
}
